import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  FaCloud, FaDatabase, FaCode, FaArrowRight, FaCheckCircle, FaProjectDiagram,
  FaChartLine, FaUsers, FaShieldAlt, FaGlobe, FaRocket, FaCogs, FaMicrochip, FaTools 
} from "react-icons/fa";
import "../styles/Home.css";

// Import Images (Ensure these paths match your project)
import netsuiteImg from "../assets/images/netsuite.jpg";
import salesforceImg from "../assets/images/salesforce.jpg";
import webdevImg from "../assets/images/webdev.jpg";
import { FaCircle } from "react-icons/fa"; // For a small separator dot

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};
const tickerServices = [
  "NetSuite ERP", "Salesforce CRM", "SuiteScript 2.1", "Apex & LWC", 
  "Next.js Engineering", "Node.js Microservices", "SaaS Architecture", 
  "Cloud DevOps", "OneWorld Global", "Custom APIs", "ERP Migration", 
  "AI & Agentforce", "Data Cloud", "UI/UX Design"
];
const homeFaqs = [
  {
    q: "What services does ITSeva Hub provide for enterprises?",
    a: "ITSeva Hub specializes in high-end cloud engineering, specifically Oracle NetSuite ERP (SuiteScript 2.1), Salesforce CRM (Apex/LWC), and Full-Stack Web Development using Next.js and Node.js."
  },
  {
    q: "Do you offer global NetSuite OneWorld implementation?",
    a: "Yes, we architect multi-subsidiary environments for global firms, ensuring localized tax compliance, intercompany eliminations, and consolidated financial reporting."
  },
  {
    q: "Can you build custom SaaS platforms integrated with CRM/ERP?",
    a: "Absolutely. We specialize in building 'Digital Backbones' where custom web portals communicate seamlessly with your Salesforce and NetSuite systems via secure APIs."
  }
];


export default function Home() {
  // const [isAskOpen, setIsAskOpen] = useState(false);
  // const [activeFaq, setActiveFaq] = useState(null);

  // --- SEO SCHEMAS ---
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "ITSeva Hub",
    "url": "https://www.itseva-hub.com",
    "logo": "https://www.itseva-hub.com/logo.png",
    "image": "https://www.itseva-hub.com/og-image.jpg",
    "description": "Enterprise-grade technology partner specializing in NetSuite ERP, Salesforce CRM, and Full-Stack Web Engineering.",
    "address": { "@type": "PostalAddress", "addressCountry": "Global" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Cloud Engineering Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "NetSuite SuiteScript 2.1 Development" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Salesforce LWC & Apex Architect" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Next.js SaaS Web Development" } }
      ]
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": homeFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };
  return (
    <main className="home-v3 animated-bg">
      <Helmet>
        {/* Basic SEO */}
        <title>ITSeva Hub | Global NetSuite Consultant, Salesforce Partner & Web Architect</title>
        <meta name="description" content="Certified NetSuite SuiteScript 2.1 developers and Salesforce LWC architects. We engineer digital backbones for global enterprises using Next.js and Cloud ERP." />
        <meta name="keywords" content="NetSuite Consultant, Salesforce Partner, SuiteScript 2.1, LWC Developer, Next.js SaaS, Enterprise ERP, CRM Automation" />
        <link rel="canonical" href="https://www.itseva-hub.com" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ITSeva Hub | Global Technology Architects" />
        <meta property="og:description" content="Architecting the digital backbone for modern enterprises through NetSuite, Salesforce, and Web Engineering." />
        <meta property="og:image" content="https://www.itseva-hub.com/og-image.jpg" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="animated-mesh"></div>

      {/* --- HERO SECTION --- */}
      <section className="hero-fullscreen">
        <div className="container">
          <motion.div className="hero-overlay-text" initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="badge-modern">
              <FaRocket /> Enterprise Technology Partner
            </motion.div>
            <motion.h1 variants={fadeInUp}>
              Architecting the Digital <span className="text-gradient">Backbone</span> <br />
              through <Link to="/netsuite" className="link-span ns">NetSuite</Link>, 
              <Link to="/salesforce" className="link-span sf">Salesforce</Link> & 
              <Link to="/web-development" className="link-span wb">Web Engineering</Link>
            </motion.h1>
            <motion.p variants={fadeInUp} className="hero-description">
              Driving global scalability with high-performance <strong>ERP</strong>, <strong>CRM</strong>, 
              and <strong>SaaS ecosystems</strong> built for modern enterprise efficiency.
            </motion.p>
          </motion.div>
        </div>
      </section>
      {/* --- AUTO SCROLLING TICKER SECTION --- */}
      <section className="service-ticker-section">
        <div className="ticker-container">
          <motion.div 
            className="ticker-track"
            animate={{ x: [0, -1920] }} // Adjust distance based on content width
            transition={{ 
              x: {
                repeat: Infinity, 
                repeatType: "loop", 
                duration: 30, 
                ease: "linear" 
              } 
            }}
          >
            {/* Render the list twice for seamless looping */}
            {[...tickerServices, ...tickerServices].map((service, index) => (
              <div key={index} className="ticker-item">
                <FaCircle className="ticker-dot" />
                <span>{service}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- NEW SECTION: CORE SERVICES VISUAL HUB --- */}
      <section className="services-visual-hub">
        <div className="container">
          <div className="section-title center">
            <span className="accent-text">Our Specializations</span>
            <h2>Enterprise Solutions Hub</h2>
          </div>
          
          <div className="visual-grid">
            {[
              { 
                to: "/netsuite", 
                title: "NetSuite ERP", 
                desc: "SuiteScript 2.1 automation & global OneWorld architecture.", 
                img: netsuiteImg, 
                icon: <FaCloud /> 
              },
              { 
                to: "/salesforce", 
                title: "Salesforce CRM", 
                desc: "High-performance Apex, LWC, and Agentforce AI implementations.", 
                img: salesforceImg, 
                icon: <FaDatabase /> 
              },
              { 
                to: "/web-development", 
                title: "Web Engineering", 
                desc: "Full-stack Next.js, Node.js, and cloud-native SaaS platforms.", 
                img: webdevImg, 
                icon: <FaCode /> 
              }
            ].map((service, i) => (
              <motion.div 
                key={i}
                className="visual-card"
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={service.to} className="card-link">
                  <div className="card-image-wrapper">
                    <img src={service.img} alt={service.title} />
                    <div className="card-overlay"></div>
                  </div>
                  <div className="card-content">
                    <div className="card-icon-box">{service.icon}</div>
                    <h3>{service.title}</h3>
                    <p>{service.desc}</p>
                    <span className="learn-more">
                      Explore Expertise <FaArrowRight />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- GLOBAL ENTERPRISE SCALE --- */}
      <section className="global-scale">
        <div className="container">
          <div className="scale-grid glass">
            <div className="scale-content">
              <span className="accent-text">Global Infrastructure</span>
              <h2>Multi-Subsidiary <span className="text-gradient">Orchestration</span></h2>
              <p>We specialize in <strong>NetSuite OneWorld</strong> and global <strong>Salesforce Multi-Org</strong> architectures. From automated intercompany eliminations to localized tax compliance.</p>
              <div className="scale-stats">
                <div className="s-stat"><h4>15+</h4><p>Countries Supported</p></div>
                <div className="s-stat"><h4>24/7</h4><p>Support Cycle</p></div>
              </div>
            </div>
            <div className="scale-visual">
              <FaGlobe className="bg-icon-fade" />
              <div className="floating-badge">SuiteTax Ready</div>
            </div>
          </div>
        </div>
      </section>

      {/* --- INDUSTRY VERTICALS --- */}
      <section className="industry-verticals">
        <div className="container">
          <div className="section-title center">
            <span className="accent-text">Strategic Focus</span>
            <h2>Tailored Industry Solutions</h2>
          </div>
          <div className="industry-grid">
            {[
              { title: "Wholesale & Distribution", desc: "Automating Order-to-Cash (O2C) and Procure-to-Pay (P2P) cycles with real-time inventory sync." },
              { title: "SaaS & Software", desc: "Complex revenue recognition (ARM) and subscription billing lifecycle management." },
              { title: "Fintech & Banking", desc: "Secure API-first architectures with high-concurrency Node.js and AWS infrastructure." },
              { title: "Global Manufacturing", desc: "Work order automation and shop-floor visibility integrated with core financial ERP." }
            ].map((ind, i) => (
              <div className="industry-card glass" key={i}>
                <h4>{ind.title}</h4>
                <p>{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- METHODOLOGY --- */}
      <section className="methodology">
        <div className="container">
          <div className="section-title">
            <span className="accent-text">Expert Methodology</span>
            <h2>The Engineering Roadmap</h2>
          </div>
          <div className="methodology-grid">
            {[
              { t: "Technical Audit", d: "Deep dive into existing SuiteScript and Apex logic to identify bottlenecks.", icon: <FaCogs /> },
              { t: "Architecture", d: "Designing API-first environments and scalable cloud-native schemas.", icon: <FaProjectDiagram /> },
              { t: "Engineering", d: "Agile delivery of modern SaaS and automated ERP financial cycles.", icon: <FaCode /> },
              { t: "Managed Scale", d: "24/7 post-deployment oversight and global subsidiary expansion.", icon: <FaChartLine /> }
            ].map((step, i) => (
              <div key={i} className="method-card">
                <div className="step-number">0{i+1}</div>
                <div className="m-icon">{step.icon}</div>
                <h4>{step.t}</h4>
                <p>{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TECH STACK HUB --- */}
      <section className="tech-stack-hub">
        <div className="container">
          <div className="stack-box glass">
            <div className="stack-header">
              <FaMicrochip />
              <h3>The Modern Engineering Stack</h3>
            </div>
            <div className="stack-grid">
              <div className="stack-col">
                <h4>ERP & CRM</h4>
                <ul><li>SuiteScript 2.1</li><li>Apex & LWC</li><li>SuiteTalk REST</li><li>Salesforce Agentforce</li></ul>
              </div>
              <div className="stack-col">
                <h4>Full-Stack</h4>
                <ul><li>Next.js 15</li><li>Node.js / NestJS</li><li>TypeScript</li><li>GraphQL / Prisma</li></ul>
              </div>
              <div className="stack-col">
                <h4>Cloud & Data</h4>
                <ul><li>AWS / Vercel</li><li>Docker / Kubernetes</li><li>PostgreSQL</li><li>Data Cloud</li></ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- PERFORMANCE METRICS --- */}
      <section className="impact-stats">
        <div className="container">
          <div className="stats-strip glass">
            <div className="stat-item">
              <h4>80%</h4>
              <p>Manual Entry Reduction</p>
            </div>
            <div className="stat-item">
              <h4>1.2s</h4>
              <p>Avg. Web Load Time</p>
            </div>
            <div className="stat-item">
              <h4>40%</h4>
              <p>Deployment Velocity Increase</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- ACTION STRIP --- */}
      <footer className="footer-action-strip">
        <div className="container strip-content">
          <div className="cta-text">
            <h3>Ready to modernize your technology stack?</h3>
            <p>Join the global enterprises scaling with ITSeva Hub.</p>
          </div>
          <Link to="/contact" className="btn-main-glow">Get Technical Audit</Link>
        </div>
      </footer>
    </main>
  );
}